import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/app/api";
import type {
  SeedModuleResponse,
  SeedVariableInfo,
  SeedPreviewRequest,
  SeedPreviewResponse,
  SeedExecuteRequest,
  SeedExecuteResponse,
  SeedYAMLUploadRequest,
} from "@/app/api/types/seeding";
import { seedingKeys } from "@/query/keys/seeding";

// List available seed modules
export function useSeedModules() {
  return useQuery({
    queryKey: seedingKeys.modules(),
    queryFn: () => api.seeding.listModules(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Get variables for selected modules
export function useSeedModuleVariables(modules: string[]) {
  return useQuery({
    queryKey: seedingKeys.variables(modules),
    queryFn: () => api.seeding.getModuleVariables(modules),
    enabled: modules.length > 0,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

// Preview seeding (dry run)
export function useSeedPreview() {
  return useMutation({
    mutationFn: (request: SeedPreviewRequest) =>
      api.seeding.previewSeed(request),
  });
}

// Execute seeding
export function useSeedExecute() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: SeedExecuteRequest) =>
      api.seeding.executeSeed(request),
    onSuccess: () => {
      // Invalidate relevant queries after seeding
      // This ensures the app shows the newly created data
      queryClient.invalidateQueries({ queryKey: ["companies"] });
      queryClient.invalidateQueries({ queryKey: ["products"] });
      queryClient.invalidateQueries({ queryKey: ["partners"] });
      queryClient.invalidateQueries({ queryKey: ["users"] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
  });
}

// Seed from YAML upload
export function useSeedFromYAML() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: SeedYAMLUploadRequest) =>
      api.seeding.seedFromYAML(request),
    onSuccess: () => {
      // Invalidate relevant queries after seeding
      queryClient.invalidateQueries({ queryKey: ["companies"] });
      queryClient.invalidateQueries({ queryKey: ["products"] });
      queryClient.invalidateQueries({ queryKey: ["partners"] });
      queryClient.invalidateQueries({ queryKey: ["users"] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
  });
}
